import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';

/**
 * Required "I agree" tick box for account creation (2026-09-28) — Sign up (email + every social
 * button) and Join (invited users). The two documents open in a new tab so the half-filled form is
 * never lost. [showError] turns the box red with a small shake when someone tries to continue
 * without ticking — a clearer hint than a silently greyed-out button.
 *
 *   <app-legal-agree [(checked)]="agreed" [attempt]="agreeAttempts"></app-legal-agree>
 */
@Component({
  selector: 'app-legal-agree',
  standalone: true,
  imports: [SHARED_MODULES],
  template: `
    <div class="la" [class.la--error]="showError && !checked" [class.la--shake]="shaking">
      <p-checkbox [binary]="true" [ngModel]="checked" (ngModelChange)="onChange($event)" [inputId]="inputId"></p-checkbox>
      <label [for]="inputId" class="la-text">
        {{ 'legal.agree.prefix' | t }}
        <a href="/legal/terms" target="_blank" rel="noopener" (click)="$event.stopPropagation()">{{ 'legal.agree.terms' | t }}</a>
        {{ 'legal.agree.and' | t }}
        <a href="/legal/privacy" target="_blank" rel="noopener" (click)="$event.stopPropagation()">{{ 'legal.agree.privacy' | t }}</a>
      </label>
    </div>
    <small class="la-msg" *ngIf="showError && !checked" role="alert">
      <i class="pi pi-exclamation-circle"></i>{{ 'legal.agree.required' | t }}
    </small>
  `,
  styles: [`
    :host { display: block; }
    .la {
      display: flex; align-items: flex-start; gap: 0.6rem; padding: 0.55rem 0.7rem; border-radius: 10px;
      border: 1px solid transparent; transition: border-color 0.2s ease, background-color 0.2s ease;
    }
    .la--error {
      border-color: color-mix(in srgb, var(--red-500) 45%, var(--surface-border));
      background: color-mix(in srgb, var(--red-500) 6%, transparent);
    }
    .la--shake { animation: la-shake 0.4s ease; }
    .la-text { font-size: 0.85rem; line-height: 1.45; color: var(--text-color); cursor: pointer; text-align: left; }
    .la-text a { color: var(--primary-color); font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
    .la-msg { display: inline-flex; align-items: center; gap: 0.35rem; margin: 0.3rem 0 0 0.7rem; font-size: 0.78rem; color: var(--red-500); }
    .la-msg i { font-size: 0.78rem; }
    @keyframes la-shake {
      0%, 100% { transform: none; } 20% { transform: translateX(-5px); } 40% { transform: translateX(5px); }
      60% { transform: translateX(-3px); } 80% { transform: translateX(3px); }
    }
    @media (prefers-reduced-motion: reduce) { .la--shake { animation: none; } }
  `],
})
export class LegalAgreeComponent {
  @Input() checked = false;
  @Output() checkedChange = new EventEmitter<boolean>();
  /** Bumped by the page every time someone tries to continue without ticking: shows the red
   *  "please tick" state and shakes again on every such try (a plain boolean would shake once). */
  @Input() set attempt(n: number) {
    this._attempt = n || 0;
    if (this._attempt > 0 && !this.checked) {
      this.shaking = false;
      setTimeout(() => (this.shaking = true));
      setTimeout(() => (this.shaking = false), 450);
    }
  }
  get showError(): boolean { return this._attempt > 0; }
  private _attempt = 0;
  shaking = false;
  readonly inputId = 'la-' + Math.random().toString(36).slice(2, 9);

  onChange(v: boolean): void {
    this.checked = !!v;
    this.checkedChange.emit(this.checked);
  }
}
